import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import type { ModuleKey, PageKey } from '@erp/rpc';
import type { Message, MessageToolResult } from '@/context/agent-state.context';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';

export interface AgentModuleConfig {
  moduleKey: ModuleKey;
  /**
   * Optional page-level identifier resolved against the backend page
   * registry. Layered on top of the module to provide page-specific prompt
   * fragments and tools.
   */
  pageKey?: PageKey;
  currentPage: string;
  getModuleContext?: () => Record<string, unknown> | undefined;
  renderToolResult?: (
    message: Message,
    toolResult: MessageToolResult,
  ) => ReactNode;
  /** Frontend-defined tools — schemas sent to backend, execution on client */
  tools?: FrontendToolDefinition[];
  placeholder?: string;
  /**
   * Optional prompts offered as one-click chips in the chat's empty state.
   * Clicking one sends it like a typed message. Pass a stable array.
   */
  suggestions?: string[];
  headerTitle?: string;
  headerIcon?: ReactNode;
  onFocusNodes?: (nodeIds: string[]) => void;
}

interface AgentContainerContextData {
  containerElement: HTMLElement | null;
  moduleConfig: AgentModuleConfig | null;
  /** Ref-based accessors for callbacks that must be fresh at call-time */
  moduleConfigRef: React.RefObject<AgentModuleConfig | null>;
  registerContainer: (element: HTMLElement) => void;
  unregisterContainer: () => void;
  setModuleConfig: (config: AgentModuleConfig) => void;
  clearModuleConfig: () => void;
}

const AgentContainerContext = createContext<
  AgentContainerContextData | undefined
>(undefined);

export function AgentContainerProvider({ children }: PropsWithChildren) {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null,
  );
  const [moduleConfig, setModuleConfigState] =
    useState<AgentModuleConfig | null>(null);
  const moduleConfigRef = useRef<AgentModuleConfig | null>(null);

  const registerContainer = useCallback((element: HTMLElement) => {
    setContainerElement(element);
  }, []);

  const unregisterContainer = useCallback(() => {
    setContainerElement(null);
  }, []);

  const setModuleConfig = useCallback((config: AgentModuleConfig) => {
    moduleConfigRef.current = config;
    setModuleConfigState(config);
  }, []);

  const clearModuleConfig = useCallback(() => {
    moduleConfigRef.current = null;
    setModuleConfigState(null);
  }, []);

  return (
    <AgentContainerContext.Provider
      value={{
        containerElement,
        moduleConfig,
        moduleConfigRef,
        registerContainer,
        unregisterContainer,
        setModuleConfig,
        clearModuleConfig,
      }}
    >
      {children}
    </AgentContainerContext.Provider>
  );
}

export function useAgentContainer() {
  const context = useContext(AgentContainerContext);
  if (context === undefined) {
    throw new Error(
      'useAgentContainer must be used within an AgentContainerProvider',
    );
  }
  return context;
}
